/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './clouserReport.scss';
import PropTypes from 'prop-types';
import DataGridComponent from '../../../../Components/DataGrid/dataGrid';
import UseAxios from '../../../../Api/http/http';
import { CLOUSER_REPORT, DATE_NOTE_HEADING, DATE_NOTE_DESCRIPTION } from '../../../../Constants/strings';
import { getClosureReportExcel } from '../../../../Api/request/reports';
import { getClosureReport } from '../../../../Api/request/misReports';
import DateRangeEditor from '../../../../Components/CustomDateRange/dateRangeInput';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ButtonComponent from '../../../../Components/Button/button';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';
import ArrowDropDown from '../../../../Assets/images/svg/drop-down-arrow.svg';
import excelIcon from '../../../../Assets/images/svg/excel.svg';
import excelhoverIcon from '../../../../Assets/images/svg/excel_hover.svg';
import { defaultDateRanges } from '../defaultDate';
import moment from 'moment';

const ClouserReport = ({ dateRange: globalDateRange, dealType }) => {
  const [rows, setRows] = useState([]);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1024);
  const [isExcelHovered, setIsExcelHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { snackbarOpen } = useSnackbar();

  const initialDateRange = defaultDateRanges.find(r => r.label === 'This Week') || {};
  const [dateRange, setDateRange] = useState({
    startDate: initialDateRange.startDate,
    endDate: initialDateRange.endDate,
    label: initialDateRange.label,
  });

   useEffect(() => {
    if (globalDateRange?.startDate && globalDateRange?.endDate) {
      setDateRange(globalDateRange);
    }
  }, [globalDateRange]);
  
  const formatDate = useCallback(date => (date ? moment(date).format('YYYY-MM-DD') : null), []);

  const handleDateRangeChange = newRange => setDateRange(newRange);
  const handleDownloadClick = event => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const fetchClosureReport = useCallback(async () => {
    try {
      const formattedStartDate = formatDate(dateRange.startDate);
      const formattedEndDate = formatDate(dateRange.endDate);
      const { data = [] } = await UseAxios(getClosureReport({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        dealType,
      }));
      setRows(data.map((row, i) => ({ ...row, id: row.uuid || row.positionId || i })));
    } catch (error) {
      console.error('Error fetching closure report:', error);
    }
  }, [dateRange, dealType, formatDate]);

  const handleDownloadExcel = async () => {
    try {
      if (!dateRange) return;
      await getClosureReportExcel(dateRange.startDate, dateRange.endDate);
    } catch (error) {
      snackbarOpen(CLOUSER_REPORT.DOWNLOAD_FAILURE_MESSAGE, CLOUSER_REPORT.ERROR_SEVERITY);
    } finally {
      handleCloseMenu();
    }
  };

  const checkMobileView = useCallback(() => {
    setIsMobileView(window.innerWidth < 1024);
  }, []);

  useEffect(() => {
    fetchClosureReport();
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, [fetchClosureReport, checkMobileView]);

  const columns = useMemo(() => [
    {
      field: CLOUSER_REPORT.FIELDS.CUSTOMER,
      headerName: CLOUSER_REPORT.HEADER.CUSTOMER,
      ...(isMobileView ? { width: 140 } : { flex: 0.15 }),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CLOUSER_REPORT.FIELDS.DEAL_TYPE,
      headerName: CLOUSER_REPORT.HEADER.DEAL_TYPE,
      ...(isMobileView ? { width: 120 } : { flex: 0.1 }),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CLOUSER_REPORT.FIELDS.GEOGRAPHY,
      headerName: CLOUSER_REPORT.HEADER.GEOGRAPHY,
      ...(isMobileView ? { width: 120 } : { flex: 0.1 }),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CLOUSER_REPORT.FIELDS.VERTICAL,
      headerName: CLOUSER_REPORT.HEADER.VERTICAL,
      ...(isMobileView ? { width: 100 } : { flex: 0.1 }),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CLOUSER_REPORT.FIELDS.LOCATION,
      headerName: CLOUSER_REPORT.HEADER.LOCATION,
      ...(isMobileView ? { width: 120 } : { flex: 0.1 }),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CLOUSER_REPORT.FIELDS.NO_OF_POSITIONS,
      headerName: CLOUSER_REPORT.HEADER.NO_OF_POSITIONS,
      ...(isMobileView ? { width: 160 } : { flex: 0.12 }),
      sortable: false,
      disableColumnMenu: true,
      cellClassName: 'center-align-cell',
    },
    {
      field: CLOUSER_REPORT.FIELDS.DETAILS,
      headerName: CLOUSER_REPORT.HEADER.DETAILS,
      ...(isMobileView ? { width: 120 } : { flex: 0.2 }),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CLOUSER_REPORT.FIELDS.SALES_TEAM_MEMBER,
      headerName: CLOUSER_REPORT.HEADER.SALES_TEAM_MEMBER,
      ...(isMobileView ? { width: 185 } : { flex: 0.15 }),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CLOUSER_REPORT.FIELDS.DELIVERY_MANAGER,
      headerName: CLOUSER_REPORT.HEADER.DELIVERY_MANAGER,
      ...(isMobileView ? { width: 170 } : { flex: 0.15 }),
      sortable: false,
      disableColumnMenu: true,
    },
  ], [isMobileView]);

  const sortFilterColumns = useMemo(() => columns.map(c => c.field), [columns]);

  return (
    <div className="closure-report-outer-layer">
      <div className="closure-report">
        <div className="closure-top-section">
          <div className="closure-date-filter">
            <DateRangeEditor
              key={`weekly-closure-date-range-${dateRange.startDate}-${dateRange.endDate}`}
              dateRange={{
                startDate: dateRange.startDate ? new Date(dateRange.startDate) : null,
                endDate: dateRange.endDate ? new Date(dateRange.endDate) : null,
              }}
              onChange={(newDateRange) => handleDateRangeChange(newDateRange)}
              fromPlaceholder="From Date"
              toPlaceholder="To Date"
              dateFormat="dd/mm/yyyy"
              definedRanges={defaultDateRanges}
              useCurrentYear
              splitInitialCustomMonthView={true}
            />
            <div className="staff-aug-date-range-note">
              <strong className="staff-aug-date-heading">{DATE_NOTE_HEADING}</strong> {DATE_NOTE_DESCRIPTION}
            </div>
          </div>
          <div>
            <ButtonComponent
              label="Download"
              variant="text"
              onClick={handleDownloadClick}
              endIcon={<img alt="Arrow Down" src={ArrowDropDown} />}
              btnCss="download-skill-btn"
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
              <MenuItem
                onClick={handleDownloadExcel}
                className="download-dropdown"
                onMouseEnter={() => setIsExcelHovered(true)}
                onMouseLeave={() => setIsExcelHovered(false)}
              >
                <img src={isExcelHovered ? excelhoverIcon : excelIcon} alt="Excel Icon" style={{ marginRight: 8 }} />
                Download as Excel
              </MenuItem>
            </Menu>
          </div>
        </div>
        <div className="closure-report-grid-container">
          <DataGridComponent
            dataRows={rows}
            dataColumns={columns}
            sortFilterColumns={sortFilterColumns}
            pageSize={10}
            isEdit={false}
            dataGridCss="closure-report-grid"
          />
        </div>
      </div>
    </div>
  );
};

ClouserReport.propTypes = {
  dateRange: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    label: PropTypes.string
  }),
  dealType: PropTypes.arrayOf(PropTypes.string)
};

export default ClouserReport;
